import { createSelector } from 'reselect'
import { IApplicationState } from '@Site/rootReducer'
import find from 'lodash/find'
import filter from 'lodash/filter'
import map from 'lodash/map'
import some from 'lodash/some'
import uniq from 'lodash/uniq'
import flatMapDepth from 'lodash/flatMapDepth'

import {
  getWorkspaces,
} from '@SiteModules/Workspace/selectors'

import {
  getProjects,
  getProjectVersions,
} from '@SiteModules/Project/selectors'

export const getEditingSubtitleIds = (state: IApplicationState): number[] => state.popup.editingSubtitleIds
export const getEditingProjectIds = (state: IApplicationState): string[] => state.popup.editingProjectIds
export const getEditingProjectVersionIds = (state: IApplicationState): string[] => state.popup.editingProjectVersionIds
export const getProjectType =  (state: IApplicationState): string | undefined => state.popup.projectType
export const getAccountPopupIsOpen = (state: IApplicationState): boolean => state.popup.openAccountPopup
export const getNewProjectPopupIsOpen = (state: IApplicationState): boolean => state.popup.openNewProjectPopup
export const getSetupProjectPopupIsOpen = (state: IApplicationState): boolean => state.popup.openSetupProjectPopup
export const getSetupProjectPopupTtsIsOpen = (state: IApplicationState): boolean => state.popup.openSetupProjectPopupTts
export const getImportSrtPopupIsOpen = (state: IApplicationState): boolean => state.popup.openImportSrtPopup
export const getCreateLabelPopupIsOpen = (state: IApplicationState): boolean => state.popup.openCreateLabelPopup
export const getExternalTokenPopupIsOpen = (state: IApplicationState): boolean => state.popup.openExternalTokenPopup
export const getProjectSharingPopupIsOpen = (state: IApplicationState): boolean => state.popup.openProjectSharingPopup
export const getDeleteProjectPopupIsOpen = (state: IApplicationState): boolean => state.popup.openDeleteProjectPopup
export const getDeleteProjectVersionPopupIsOpen = (state: IApplicationState): boolean => state.popup.openDeleteProjectVersionPopup
export const getRenameProjectPopupIsOpen = (state: IApplicationState): boolean => state.popup.openRenameProjectPopup
export const getRenameProjectVersionPopupIsOpen = (state: IApplicationState): boolean => state.popup.openRenameProjectVersionPopup
export const getExportationPopupIsOpen = (state: IApplicationState): boolean => state.popup.openExportationPopup
export const getDeleteLabelPopupIsOpen = (state: IApplicationState): boolean => state.popup.openDeleteLabelPopup
export const getRenameLabelPopupIsOpen = (state: IApplicationState): boolean => state.popup.openRenameLabelPopup
export const getLabelsManagerPopupIsOpen = (state: IApplicationState): boolean => state.popup.openLabelsManagerPopup
export const getNewsletterPopupIsOpen = (state: IApplicationState): boolean => state.popup.openNewsletterPopup
export const getMoveToOtherWorkspacePopupIsOpen = (state: IApplicationState): boolean => state.popup.openMoveToOtherWorkspacePopup
export const getDeleteWorkspacePopupIsOpen = (state: IApplicationState): boolean => state.popup.openDeleteWorkspacePopup
export const getCreateWorkspacePopupIsOpen = (state: IApplicationState): boolean => state.popup.openCreateWorkspacePopup
export const getCreateAccountUserPopupIsOpen = (state: IApplicationState): boolean => state.popup.openCreateAccountUserPopup
export const getManageWorkspaceAccessPopupIsOpen = (state: IApplicationState): boolean => state.popup.openManageWorkspaceAccessPopup
export const getRenewSubscriptionPopupIsOpen = (state: IApplicationState): boolean => state.popup.openRenewSubscriptionPopup
export const getEditingWorkspaceIds = (state: IApplicationState): number[] => state.popup.editingWorkspaceIds
export const getFirstEditingWorkspaceId = (state: IApplicationState): number => state.popup.editingWorkspaceIds[0] ?? -1
export const getVoiceTypeUpgradePopupLabel = (state: IApplicationState): string | null => state.popup.voiceTypeUpgradePopupLabel
export const getRemoveSpeakerVoicePopupIsOpen = (state: IApplicationState): boolean => state.popup.openRemoveSpeakerVoicePopup
export const getEditingSpeakerVoiceIds = (state: IApplicationState): string[] => state.popup.editingSpeakerVoiceIds
export const getFirstEditingProjectId = (state: IApplicationState): string => state.popup.editingProjectIds[0] ?? -1
export const getFirstEditingProjectVersionId = (state: IApplicationState): string => state.popup.editingProjectVersionIds[0] ?? -1

export const getAnyPopupThatCanBeOpenedFromProjectVersionsOrLanguagesViewIsOpen = (state: IApplicationState): boolean => {
  return (
    state.popup.openImportSrtPopup ||
    state.popup.openDeleteProjectVersionPopup ||
    state.popup.openExternalTokenPopup ||
    state.popup.openProjectSharingPopup ||
    state.popup.openRenewSubscriptionPopup ||
    state.popup.openSetupProjectPopup ||
    state.popup.openSetupProjectPopupTts ||
    state.popup.openRenameProjectVersionPopup
  )
}

export const getFirstEditingWorkspace = createSelector(
  [getFirstEditingWorkspaceId, getWorkspaces],
  (editingWorkspaceId, workspaces) => find(workspaces, workspace => workspace.id === editingWorkspaceId)
)

export const getFirstEditingWorkspaceName = createSelector(
  [getFirstEditingWorkspace],
  (editingWorkspace) => editingWorkspace ? editingWorkspace.attributes.name : ''
)

export const getFirstEditingProject = createSelector(
  [getFirstEditingProjectId, getProjects],
  (editingProjectId, projects) => find(projects, project => project.id === editingProjectId)
)

export const getFirstEditingProjectTitle = createSelector(
  [getFirstEditingProject],
  (editingProject) => editingProject ? editingProject.attributes.title : ''
)

export const getFirstEditingProjectVersion = createSelector(
  [getFirstEditingProjectVersionId, getProjectVersions],
  (editingProjectVersionId, projectVersions) => find(projectVersions, projectVersion => projectVersion.id === editingProjectVersionId)
)

export const getFirstEditingProjectVersionTitle = createSelector(
  [getFirstEditingProjectVersion],
  (editingProjectVersion) => editingProjectVersion ? editingProjectVersion.attributes.title : ''
)

export const getProjectIdOfFirstEditingProjectVersion = createSelector(
  [getFirstEditingProjectVersion],
  (editingProjectVersion) => editingProjectVersion ? editingProjectVersion.attributes.projectId : ''
)

export const getEditingProjects = createSelector(
  [getEditingProjectIds, getProjects],
  (editingProjectIds, projects) => filter(projects, project => editingProjectIds.includes(project.id))
)

export const getAtLeastOneEditingProjectHasClips = createSelector(
  [getEditingProjects],
  (editingProjects => some(editingProjects, editingProject => some(editingProject.projectVersions, projectVersion => projectVersion.attributes.isRepurposed)))
)

export const getAllLanguagesOfEditingProjects = createSelector(
  [getEditingProjects], editingProjects => uniq(flatMapDepth(editingProjects, editingProject => map(editingProject.projectVersions, projectVersion => map(projectVersion.subtitles, subtitle => subtitle.attributes.language)), 2))
)
